<script setup>
import axios from '@axios'
import moment from 'moment'
import { computed, onMounted, ref, watch } from 'vue'
import { useDebounce } from '@vueuse/core'
import { accessLabel, accessOf } from '@/utils/textingActor'
import { useUsersStore } from '@/stores'
import MemberDetailsDialog from '@core/components/MemberDetailsDialog.vue'
import SmsConsentWaysPanel from '@core/components/SmsConsentWaysPanel.vue'
import ActivateOnRequestDialog from '@core/components/ActivateOnRequestDialog.vue'

/*
 * RM-205 Phase 3 — admin: texting with RM AI, per member.
 *
 * Three jobs, in priority order:
 *   1. flip texting on/off for a member (the rollout switch — policy for who
 *      gets it is still an explicit admin call, not a tier rule)
 *   2. see who is texting-ready (phone verified via the emailed link)
 *   3. resolve a shared number (an admin's phone on their boss's account):
 *      grant phone-share consent so that member may verify it too
 *
 * Verification itself is never done here — it is lazy and member-driven
 * (first text → confirmation link to the approved email). This page shows the
 * result and unblocks the two things an admin controls.
 */

const search = ref('')
const debouncedSearch = useDebounce(search, 300)
const filter = ref('members')
const loading = ref(false)
const saving = ref({})
const members = ref([])
const error = ref('')

// Kapil 10/3: every member is in one group: Members (not invited yet),
// Invited, Active. A-Z by first name is the natural order, or most bookings.
// The server works out each member's status (`textingState`), so a group and
// the chip on its rows always agree.
const FILTERS = [
  { value: 'members', label: 'Members' },
  { value: 'invited', label: 'Invited' },
  { value: 'active', label: 'Active' },
]
const sort = ref('name')
const SORTS = [
  { value: 'name', label: 'A–Z' },
  { value: 'bookings', label: 'Most bookings' },
]
const bookingsLabel = n => `${n} booking${n === 1 ? '' : 's'}`
// The search looks only within the group that is selected, and says so.
const searchPlaceholder = computed(() => `Search ${(FILTERS.find(f => f.value === filter.value) || FILTERS[0]).label}: name, email or phone`)

// Kapil 9/26: two tabs — Platinum members, and the restaurant team people who
// receive reservation referrals (from each restaurant's Profile & Settings).
const tab = ref('members')
const team = ref([])
const teamLoading = ref(false)
const teamLoaded = ref(false)
async function loadTeam() {
  teamLoading.value = true
  error.value = ''
  try {
    const res = await axios.get('/agent/admin/sms-restaurant-team')
    // remember what each row showed on load so a focus change alone never writes
    team.value = (res.data.people || []).map(p => ({ ...p, _shown: { name: p.name || '', email: p.email || '' } }))
    teamLoaded.value = true
  } catch (e) {
    error.value = e.response?.data?.error || 'Could not load the restaurant team.'
  } finally {
    teamLoading.value = false
  }
}
const teamRows = computed(() => {
  const q = (search.value || '').trim().toLowerCase()
  if (!q) return team.value

  return team.value.filter(p => [p.name, p.phoneDisplay, p.phoneE164, ...p.restaurants.map(r => r.name), ...p.memberAccounts.map(a => a.email)]
    .some(v => String(v || '').toLowerCase().includes(q)))
})
watch(tab, t => { if (t === 'team' && !teamLoaded.value) loadTeam() })
const teamSaving = ref({})
const teamSaved = ref({})
function markTeamBusy(p, on) {
  const next = { ...teamSaving.value }
  if (on) next[p.phoneE164] = true
  else delete next[p.phoneE164]
  teamSaving.value = next
}
function flashSaved(p) {
  teamSaved.value = { ...teamSaved.value, [p.phoneE164]: Date.now() }
  setTimeout(() => { const n = { ...teamSaved.value }; delete n[p.phoneE164]; teamSaved.value = n }, 1800)
}
function applyContact(p, c) {
  if (!c) return
  if (c.name) p.name = c.name
  if (c.email) p.email = c.email
  p._shown = { name: p.name || '', email: p.email || '' }
  p.textingEnabled = !!c.textingEnabled
  p.status = c.status || null
  p.invitedAt = c.invitedAt || null
  p.consentAt = c.consentAt || null
  p.optedOutAt = c.optedOutAt || null
}
async function saveTeamPerson(p, patch) {
  const changed = Object.keys(patch).some(k => (patch[k] || '') !== ((p._shown || {})[k] || ''))
  if (!changed) return
  markTeamBusy(p, true)
  error.value = ''
  try {
    const res = await axios.patch(`/agent/admin/sms-restaurant-team/${encodeURIComponent(p.phoneE164)}`, patch)
    applyContact(p, res.data.contact)
    flashSaved(p)
  } catch (e) {
    error.value = e.response?.data?.error || 'Could not save.'
  } finally {
    markTeamBusy(p, false)
  }
}
async function setTeamTexting(p, enabled) {
  markTeamBusy(p, true)
  error.value = ''
  try {
    const res = await axios.post(`/agent/admin/sms-restaurant-team/${encodeURIComponent(p.phoneE164)}/texting`, { enabled, name: p.name, email: p.email })
    applyContact(p, res.data.contact)
    flashSaved(p)
  } catch (e) {
    error.value = e.response?.data?.error || 'Could not update texting.'
    if (e.response?.data?.contact) applyContact(p, e.response.data.contact)
  } finally {
    markTeamBusy(p, false)
  }
}
async function resendTeamInvitation(p) {
  markTeamBusy(p, true)
  error.value = ''
  try {
    const res = await axios.post(`/agent/admin/sms-restaurant-team/${encodeURIComponent(p.phoneE164)}/invite`)
    applyContact(p, res.data.contact)
    flashSaved(p)
  } catch (e) {
    error.value = e.response?.data?.error || 'Could not send the invitation.'
  } finally {
    markTeamBusy(p, false)
  }
}
// Same vocabulary and colors as a member's number (phoneStatus above).
function teamStatus(p) {
  if (p.status === 'optedOut') return { label: 'Opted out', when: p.optedOutAt, color: 'warning' }
  if (p.status === 'active') return { label: 'Verified', when: p.consentAt, color: 'success' }
  if (p.status === 'invited') return { label: 'Invited', when: p.invitedAt, color: 'info' }

  return { label: 'Not confirmed', when: null, color: undefined }
}

async function load() {
  loading.value = true
  error.value = ''
  try {
    const res = await axios.get('/agent/admin/sms-members', {
      params: { search: debouncedSearch.value, filter: filter.value, sort: sort.value },
    })
    members.value = res.data.members || []
  } catch (e) {
    error.value = e.response?.data?.error || 'Could not load members.'
  } finally {
    loading.value = false
  }
}

async function update(member, patch) {
  saving.value = { ...saving.value, [member._id]: true }
  try {
    const res = await axios.patch(`/agent/admin/sms-members/${member._id}`, patch)
    Object.assign(member, res.data.member, { sharedWith: member.sharedWith })
  } catch (e) {
    error.value = e.response?.data?.error || 'Update failed.'
  } finally {
    const next = { ...saving.value }
    delete next[member._id]
    saving.value = next
  }
}

// One status word per member — the thing an admin scans for, and the same
// status the filters use (the server's `textingState`).
const STATE_CHIPS = {
  active: { text: 'Active', color: 'success' },
  invited: { text: 'Invited', color: 'info' },
  optedOut: { text: 'Opted out', color: 'warning' },
  off: { text: 'Texting off', color: undefined },
  none: { text: 'Not confirmed', color: undefined },
}
function phoneStatus(m) {
  return STATE_CHIPS[m.textingState] || STATE_CHIPS.none
}

// Activation by invitation: enabling texting emails the invitation; this resends it.
const inviting = ref({})
async function resendInvitation(member) {
  inviting.value = { ...inviting.value, [member._id]: true }
  try {
    const res = await axios.post(`/agent/admin/sms-members/${member._id}/invite`, {})
    Object.assign(member, res.data.member)
  } catch (error) {
    console.error('Resend invitation failed:', error)
    alert(error?.response?.data?.error || 'Could not send the invitation')
  } finally {
    const next = { ...inviting.value }
    delete next[member._id]
    inviting.value = next
  }
}

const when = d => (d ? moment(d).format('MMM D, YYYY') : '')

// RM-430 part G: which ways to turn on RM via text are open (the panel above
// the tabs reports them), and turning it on because the person asked.
const openWays = ref({})
function onWays(methods) {
  openWays.value = Object.fromEntries((methods || []).map(w => [w.key, !!w.open]))
}
const activateOpen = ref(false)
const activateTarget = ref(null)
const canActivateMember = m => openWays.value.staff_recorded && m.phoneNumberE164 && !['active', 'optedOut'].includes(m.textingState)
const canActivateContact = p => openWays.value.restaurant_staff_recorded && !['active', 'optedOut'].includes(p.status)
function askActivateMember(m) {
  activateTarget.value = { kind: 'member', id: m._id, member: m, name: `${m.firstName} ${m.lastName}`.trim() || m.email, number: m.phoneNumber || m.phoneNumberE164 }
  activateOpen.value = true
}
function askActivateContact(p) {
  activateTarget.value = { kind: 'contact', phoneE164: p.phoneE164, contact: p, name: p.name || 'this contact', number: p.phoneDisplay, contactName: p.name, contactEmail: p.email }
  activateOpen.value = true
}
function onActivated(data) {
  const t = activateTarget.value
  if (!t) return
  if (t.kind === 'member' && data.member) Object.assign(t.member, data.member)
  if (t.kind === 'contact' && data.contact) {
    applyContact(t.contact, data.contact)
    flashSaved(t.contact)
  }
}

// RM-427: a member who chose RM via text on their application. It turns on by
// itself when they activate (or, if they weren't Platinum then, once they are).
const APPLICATION_OPT_IN_NOTES = {
  pending: 'Chose texting on their application - turns on when they activate',
  waiting: 'Chose texting on their application - turns on once they are Platinum',
  activating: 'Chose texting on their application - turning on now',
  skipped: 'Chose texting on their application - not turned on (number changed or already in use)',
}
const applicationOptInNote = m => {
  const o = m && m.smsApplicationOptIn
  if (!o || !o.optedInAt) return ''
  const note = APPLICATION_OPT_IN_NOTES[o.status]

  return note ? `${note} (chose ${when(o.optedInAt)})` : ''
}

// When the member last signed in or opened the app (the same date All Members shows).
const lastActiveLabel = m => (m.lastActiveAt ? `Last active ${when(m.lastActiveAt)}` : 'Not active yet')

// A member's name opens their profile in the app's member popup (Kapil 10/4),
// the one Network and messages use: load the full member, then show it.
const usersStore = useUsersStore()
const viewingMember = ref(null)
const openingProfile = ref(null) // member _id whose profile is loading
async function openProfile(m) {
  if (openingProfile.value) return
  error.value = ''
  openingProfile.value = m._id
  try {
    await usersStore.fetchUserById(m._id)
    const full = usersStore.users.find(u => u._id === m._id)
    if (full) viewingMember.value = full
    else error.value = `Could not open ${m.firstName}'s profile.`
  } finally {
    openingProfile.value = null
  }
}

// Approved texting numbers on a member's account (RM-205): other people who text RM AI for this member.
const addingFor = ref(null) // member _id whose add form is open
const addForm = ref({ name: '', email: '', phone: '', access: 'assistant' })
const numberBusy = ref({})

// RM-353: what a second number may do. Full access texts exactly as the
// member; an assistant keeps a personal admin's limits (RM-337).
const ACCESS_OPTIONS = [
  { value: 'assistant', title: 'Assistant' },
  { value: 'full', title: 'Full access' },
]
const visibleNumbers = m => (m.textingNumbers || []).filter(n => n.status !== 'removed')
function numberStatus(n) {
  if (n.optedOutAt) return { text: 'Opted out', color: 'warning' }
  if (n.status === 'active') return { text: 'Active', color: 'success' }

  return { text: 'Invited', color: 'info' }
}
function openAdd(member) {
  addingFor.value = member._id
  addForm.value = { name: '', email: '', phone: '', access: 'assistant' }
}
async function withNumberBusy(key, fn) {
  numberBusy.value = { ...numberBusy.value, [key]: true }
  try { await fn() } catch (error) {
    console.error('Texting number update failed:', error)
    alert(error?.response?.data?.error || 'Could not update the number')
  } finally {
    const next = { ...numberBusy.value }
    delete next[key]
    numberBusy.value = next
  }
}
async function addNumber(member) {
  await withNumberBusy(member._id + ':add', async () => {
    const res = await axios.post(`/agent/admin/sms-members/${member._id}/numbers`, addForm.value)
    Object.assign(member, res.data.member)
    addingFor.value = null
  })
}
async function resendNumber(member, n) {
  await withNumberBusy(member._id + ':' + n.phoneE164, async () => {
    const res = await axios.post(`/agent/admin/sms-members/${member._id}/numbers/${encodeURIComponent(n.phoneE164)}/invite`, {})
    Object.assign(member, res.data.member)
  })
}
async function setAccess(member, n, access) {
  const who = n.name || n.phoneE164
  const prompt = access === 'full'
    ? `Give ${who} full access? They will text exactly as ${member.email}: notes, saved contacts, drafts and a sign-in link to finish a request.`
    : `Limit ${who} to assistant access? They can still request and submit, but not see ${member.email}'s notes or saved contacts.`
  if (!confirm(prompt)) return
  await withNumberBusy(member._id + ':' + n.phoneE164, async () => {
    const res = await axios.patch(`/agent/admin/sms-members/${member._id}/numbers/${encodeURIComponent(n.phoneE164)}`, { access })
    Object.assign(member, res.data.member)
  })
}
async function removeNumber(member, n) {
  if (!confirm(`Remove ${n.name || n.phoneE164} from ${member.email}'s texting numbers?`)) return
  await withNumberBusy(member._id + ':' + n.phoneE164, async () => {
    const res = await axios.delete(`/agent/admin/sms-members/${member._id}/numbers/${encodeURIComponent(n.phoneE164)}`)
    Object.assign(member, res.data.member)
  })
}

const empty = computed(() => !loading.value && members.value.length === 0)

onMounted(load)
watch([debouncedSearch, filter, sort], load)
</script>

<template>
  <div class="sms-admin">
    <div class="sms-admin__head">
      <h1 class="sms-admin__title">
        RM via Text
      </h1>
      <p class="sms-admin__lede">
        Platinum members with a mobile number on file. Turn texting on for a member and RM emails them an invitation to confirm the number on their profile. Their click records consent and RM AI sends the welcome text; from then on they just text. Texting is on per member.
      </p>
    </div>

    <SmsConsentWaysPanel @change="onWays" />

    <VTabs
      v-model="tab"
      class="sms-admin__tabs"
      density="comfortable"
    >
      <VTab value="members">
        Platinum members
      </VTab>
      <VTab value="team">
        Restaurant team
      </VTab>
    </VTabs>

    <template v-if="tab === 'team'">
      <div class="sms-admin__controls">
        <VTextField
          v-model="search"
          placeholder="Search name, phone, or restaurant"
          variant="outlined"
          density="comfortable"
          clearable
          hide-details
          class="sms-admin__search"
        />
      </div>
      <p class="sms-admin__lede">
        Everyone who receives a restaurant's reservation referrals by phone, from each restaurant's Profile &amp; Settings ("Phone Numbers to receive Reservation Referrals"). One row per number, however many restaurants list it.
      </p>
      <VAlert
        v-if="error"
        type="error"
        variant="tonal"
        class="mb-4"
      >
        {{ error }}
      </VAlert>
      <div
        v-if="!teamLoading && teamRows.length === 0"
        class="sms-admin__empty"
      >
        <p class="sms-admin__empty-title">
          {{ team.length ? 'No one matches.' : 'No phone numbers on any restaurant\'s Profile & Settings yet.' }}
        </p>
      </div>
      <VTable
        v-else
        class="sms-admin__table"
        density="comfortable"
      >
        <thead>
          <tr>
            <th>Person</th>
            <th>Mobile</th>
            <th>Restaurants</th>
            <th>Email</th>
            <th>Status</th>
            <th>Texting</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="p in teamRows"
            :key="p.phoneE164"
          >
            <td class="sms-admin__team-cell">
              <VTextField
                v-model="p.name"
                placeholder="Name"
                variant="underlined"
                density="compact"
                hide-details
                class="sms-admin__team-field"
                @blur="saveTeamPerson(p, { name: p.name })"
                @keyup.enter="$event.target.blur()"
              />
            </td>
            <td>
              <div class="sms-admin__phone">
                {{ p.phoneDisplay }}
              </div>
            </td>
            <td>
              <div
                v-for="r in p.restaurants"
                :key="r._id"
              >
                {{ r.name }}<span
                  v-if="r.automatedTexts"
                  class="sms-admin__meta"
                > · automated texts</span>
              </div>
            </td>
            <td class="sms-admin__team-cell">
              <VTextField
                v-model="p.email"
                placeholder="Email"
                type="email"
                variant="underlined"
                density="compact"
                hide-details
                class="sms-admin__team-field"
                @blur="saveTeamPerson(p, { email: p.email })"
                @keyup.enter="$event.target.blur()"
              />
              <div
                v-if="p.memberAccounts.length"
                class="sms-admin__meta"
              >
                RM account: {{ p.memberAccounts.map(a => a.email).join(', ') }}
              </div>
            </td>
            <td>
              <VChip
                :color="teamStatus(p).color"
                size="x-small"
                variant="tonal"
              >
                {{ teamStatus(p).label }}
              </VChip>
              <div class="sms-admin__meta">
                <template v-if="teamStatus(p).when">{{ when(teamStatus(p).when) }}</template>
                <template v-else-if="!p.email">Add an email to invite</template>
                <template v-else>Turning texting on emails the invitation</template>
              </div>
              <VBtn
                v-if="p.status === 'invited'"
                variant="text"
                size="small"
                color="primary"
                class="px-1"
                :loading="!!teamSaving[p.phoneE164]"
                @click="resendTeamInvitation(p)"
              >
                Resend invitation
              </VBtn>
              <VBtn
                v-if="canActivateContact(p)"
                variant="text"
                size="small"
                color="primary"
                class="px-1"
                :disabled="!!teamSaving[p.phoneE164]"
                @click="askActivateContact(p)"
              >
                Turn on at their request
              </VBtn>
            </td>
            <td>
              <VSwitch
                :model-value="p.textingEnabled"
                :disabled="!!teamSaving[p.phoneE164]"
                hide-details
                density="compact"
                color="primary"
                inset
                @update:model-value="v => setTeamTexting(p, !!v)"
              />
              <div
                v-if="teamSaved[p.phoneE164]"
                class="sms-admin__meta"
              >
                Saved
              </div>
            </td>
          </tr>
        </tbody>
      </VTable>
    </template>

    <template v-else>
    <!-- Kapil 10/3: the group first, then a search within it, then the order -->
    <VChipGroup
      v-model="filter"
      mandatory
      selected-class="sms-admin__filter--on"
      class="sms-admin__groups"
    >
      <VChip
        v-for="f in FILTERS"
        :key="f.value"
        :value="f.value"
        variant="outlined"
      >
        {{ f.label }}
      </VChip>
    </VChipGroup>
    <div class="sms-admin__controls">
      <VTextField
        v-model="search"
        :placeholder="searchPlaceholder"
        variant="outlined"
        density="comfortable"
        clearable
        hide-details
        class="sms-admin__search"
      />
    </div>
    <div class="sms-admin__sort">
      <span class="sms-admin__meta">Sort</span>
      <VChipGroup
        v-model="sort"
        mandatory
        selected-class="sms-admin__filter--on"
      >
        <VChip
          v-for="s in SORTS"
          :key="s.value"
          :value="s.value"
          variant="outlined"
          size="small"
        >
          {{ s.label }}
        </VChip>
      </VChipGroup>
    </div>

    <VAlert
      v-if="error"
      type="error"
      variant="tonal"
      density="compact"
      class="mb-4"
    >
      {{ error }}
    </VAlert>

    <div
      v-if="empty"
      class="sms-admin__empty"
    >
      <p class="sms-admin__empty-title">
        {{ search ? 'No members match.' : filter === 'invited' ? 'No one is waiting on an invitation.' : filter === 'active' ? 'No one is texting with RM yet.' : 'Everyone has been invited.' }}
      </p>
      <p class="sms-admin__empty-body">
        Members appear here once they have a phone on their profile. A member is <strong>Invited</strong> once texting is on and the invitation is sent, and <strong>Active</strong> once they confirm it.
      </p>
    </div>

    <VTable
      v-else
      class="sms-admin__table"
    >
      <thead>
        <tr>
          <th>Member</th>
          <th>Phone</th>
          <th class="sms-admin__col-toggle">
            Texting
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="m in members"
          :key="m._id"
        >
          <td>
            <button
              type="button"
              class="sms-admin__name sms-admin__name-link"
              :aria-label="`Open ${m.firstName} ${m.lastName}'s profile`"
              @click="openProfile(m)"
            >
              {{ m.firstName }} {{ m.lastName }}
              <VProgressCircular
                v-if="openingProfile === m._id"
                indeterminate
                size="12"
                width="2"
                class="ml-1"
              />
            </button>
            <div class="sms-admin__meta">
              {{ m.email }}
            </div>
            <div class="sms-admin__meta">
              {{ bookingsLabel(m.bookingCount || 0) }} · {{ lastActiveLabel(m) }}
            </div>
          </td>
          <td>
            <div class="sms-admin__phone">
              {{ m.phoneNumber || m.phoneNumberE164 }}
              <VChip
                :color="phoneStatus(m).color"
                size="x-small"
                variant="tonal"
                class="ml-2"
              >
                {{ phoneStatus(m).text }}
              </VChip>
            </div>
            <div
              v-if="m.phoneVerifiedAt"
              class="sms-admin__meta"
            >
              Verified {{ when(m.phoneVerifiedAt) }}
            </div>
            <div
              v-else-if="m.phoneNumberE164"
              class="sms-admin__meta"
            >
              <template v-if="applicationOptInNote(m)">{{ applicationOptInNote(m) }}</template>
              <template v-else-if="m.smsInvitedAt">Invited {{ when(m.smsInvitedAt) }}</template>
              <template v-else>Turning texting on emails the invitation</template>
              <VBtn
                v-if="m.smsAgentEnabled"
                size="x-small"
                variant="text"
                :loading="!!inviting[m._id]"
                @click="resendInvitation(m)"
              >
                {{ m.smsInvitedAt ? 'Resend invitation' : 'Send invitation' }}
              </VBtn>
              <VBtn
                v-if="canActivateMember(m)"
                size="x-small"
                variant="text"
                color="primary"
                @click="askActivateMember(m)"
              >
                Turn on at their request
              </VBtn>
            </div>

            <!-- Shared number: the one conflict an admin has to resolve here. -->
            <div
              v-if="m.sharedWith && m.sharedWith.length"
              class="sms-admin__shared"
            >
              <div class="sms-admin__meta">
                Same number on {{ m.sharedWith.map(u => u.email).join(', ') }}
              </div>
              <VBtn
                v-if="!m.phoneShareConsentAt"
                size="x-small"
                variant="tonal"
                :loading="!!saving[m._id]"
                @click="update(m, { phoneShareConsent: true })"
              >
                Allow this member to verify it
              </VBtn>
              <div
                v-else
                class="sms-admin__meta"
              >
                Share allowed {{ when(m.phoneShareConsentAt) }}
                <VBtn
                  size="x-small"
                  variant="text"
                  :loading="!!saving[m._id]"
                  @click="update(m, { phoneShareConsent: false })"
                >
                  Revoke
                </VBtn>
              </div>
            </div>
            <!-- Other people approved to text RM AI for this member, from their own phones -->
            <div class="sms-admin__numbers">
              <div
                v-for="n in visibleNumbers(m)"
                :key="n.phoneE164"
                class="sms-admin__meta sms-admin__number"
              >
                <span>{{ n.name || n.email }} · {{ n.phoneE164 }}</span>
                <VChip
                  :color="numberStatus(n).color"
                  size="x-small"
                  variant="tonal"
                  class="ml-2"
                >
                  {{ numberStatus(n).text }}
                </VChip>
                <VChip
                  size="x-small"
                  variant="outlined"
                  :data-test="`access-${n.phoneE164}`"
                >
                  {{ accessLabel(n) }}
                </VChip>
                <VBtn
                  size="x-small"
                  variant="text"
                  :loading="!!numberBusy[m._id + ':' + n.phoneE164]"
                  @click="setAccess(m, n, accessOf(n) === 'full' ? 'assistant' : 'full')"
                >
                  {{ accessOf(n) === 'full' ? 'Change to assistant' : 'Give full access' }}
                </VBtn>
                <VBtn
                  v-if="n.status !== 'active'"
                  size="x-small"
                  variant="text"
                  :loading="!!numberBusy[m._id + ':' + n.phoneE164]"
                  @click="resendNumber(m, n)"
                >
                  Resend invitation
                </VBtn>
                <VBtn
                  size="x-small"
                  variant="text"
                  color="error"
                  :loading="!!numberBusy[m._id + ':' + n.phoneE164]"
                  @click="removeNumber(m, n)"
                >
                  Remove
                </VBtn>
              </div>
              <div
                v-if="addingFor === m._id"
                class="sms-admin__add"
              >
                <VTextField
                  v-model="addForm.name"
                  label="Name"
                  density="compact"
                  variant="outlined"
                  hide-details
                />
                <VTextField
                  v-model="addForm.email"
                  label="Their email (invitation + consent)"
                  type="email"
                  density="compact"
                  variant="outlined"
                  hide-details
                />
                <VTextField
                  v-model="addForm.phone"
                  label="US mobile"
                  type="tel"
                  density="compact"
                  variant="outlined"
                  hide-details
                />
                <VSelect
                  v-model="addForm.access"
                  :items="ACCESS_OPTIONS"
                  label="Access"
                  density="compact"
                  variant="outlined"
                  :hint="addForm.access === 'full' ? 'Texts exactly as the member' : 'Can request and submit; no notes, saved contacts or sign-in links'"
                  persistent-hint
                />
                <div class="sms-admin__add-actions">
                  <VBtn
                    size="small"
                    color="primary"
                    :loading="!!numberBusy[m._id + ':add']"
                    :disabled="!addForm.phone || !addForm.email"
                    @click="addNumber(m)"
                  >
                    Send invitation
                  </VBtn>
                  <VBtn
                    size="small"
                    variant="text"
                    @click="addingFor = null"
                  >
                    Cancel
                  </VBtn>
                </div>
              </div>
              <VBtn
                v-else
                size="x-small"
                variant="text"
                @click="openAdd(m)"
              >
                Add someone who texts for this member
              </VBtn>
            </div>
          </td>
          <td class="sms-admin__col-toggle">
            <VSwitch
              :model-value="!!m.smsAgentEnabled"
              :loading="!!saving[m._id]"
              :disabled="!!saving[m._id]"
              color="primary"
              hide-details
              inset
              density="compact"
              :aria-label="`Texting with RM AI for ${m.email}`"
              @update:model-value="v => update(m, { smsAgentEnabled: !!v })"
            />
          </td>
        </tr>
      </tbody>
    </VTable>
    </template>

    <!-- A member's profile, opened from their name -->
    <MemberDetailsDialog
      :member="viewingMember"
      @on-close="viewingMember = null"
    />

    <ActivateOnRequestDialog
      v-model="activateOpen"
      :target="activateTarget"
      @done="onActivated"
    />
  </div>
</template>

<style scoped>
.sms-admin__tabs {
  margin-bottom: 16px;
}
.sms-admin__team-cell {
  vertical-align: top;
  min-width: 180px;
}
.sms-admin__team-field :deep(input) {
  font-size: 16px;
}
.sms-admin {
  max-width: 960px;
  margin: 0 auto;
  padding: 24px 16px 48px;
}

.sms-admin__head {
  margin-bottom: 24px;
}

.sms-admin__title {
  font-size: 24px;
  font-weight: 600;
  line-height: 1.25;
  margin: 0 0 8px;
}

.sms-admin__lede {
  font-size: 14px;
  line-height: 1.5;
  color: rgba(var(--v-theme-on-surface), 0.7);
  margin: 0;
  max-width: 640px;
}

.sms-admin__controls {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px 16px;
  margin-bottom: 16px;
}

.sms-admin__search {
  flex: 1 1 280px;
  max-width: 420px;
}

/* The group chips lead; the sort sits under the search with a quiet label */
.sms-admin__groups {
  margin-bottom: 12px;
}

.sms-admin__sort {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: -4px 0 12px;
}

/* iOS zooms any input under 16px — keep the search at the body size. */
.sms-admin__search :deep(input) {
  font-size: 16px;
}

.sms-admin__table th {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(var(--v-theme-on-surface), 0.6);
}

.sms-admin__table td {
  padding-top: 12px !important;
  padding-bottom: 12px !important;
  vertical-align: top;
}

.sms-admin__col-toggle {
  width: 96px;
  text-align: center;
}

.sms-admin__name {
  font-size: 14px;
  font-weight: 600;
  line-height: 1.4;
}

/* The member's name opens their profile: it reads as a link. */
.sms-admin__name-link {
  display: inline-flex;
  align-items: center;
  padding: 0;
  border: 0;
  background: none;
  color: rgb(var(--v-theme-primary));
  text-align: start;
  cursor: pointer;
}

.sms-admin__name-link:hover,
.sms-admin__name-link:focus-visible {
  text-decoration: underline;
}

.sms-admin__phone {
  font-size: 14px;
  line-height: 1.4;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.sms-admin__meta {
  font-size: 12px;
  line-height: 1.4;
  color: rgba(var(--v-theme-on-surface), 0.6);
}

.sms-admin__shared {
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
}

.sms-admin__empty {
  padding: 40px 16px;
  text-align: center;
}

.sms-admin__empty-title {
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 8px;
}

.sms-admin__empty-body {
  font-size: 14px;
  line-height: 1.5;
  color: rgba(var(--v-theme-on-surface), 0.7);
  max-width: 480px;
  margin: 0 auto;
}

@media (max-width: 600px) {
  .sms-admin__col-toggle {
    width: 72px;
  }
}

.sms-admin__numbers { margin-top: 8px; display: flex; flex-direction: column; gap: 4px; }
.sms-admin__number { display: flex; align-items: center; flex-wrap: wrap; gap: 4px; }
.sms-admin__add { display: grid; gap: 8px; margin-top: 8px; max-width: 360px; }
.sms-admin__add :deep(input),
.sms-admin__add :deep(.v-select__selection-text) { font-size: 16px; }
.sms-admin__add-actions { display: flex; gap: 8px; }
</style>

<route lang="yaml">
meta:
  layout: website
  action: read
  subject: AclDemo
  allowLoggedInOnly: true
  # Platform-admin-only: shows member phone numbers and controls who may text
  # RM AI. The API enforces requireAdmin() on every /agent/admin/sms-members
  # endpoint; gating the route stops non-admins from ever loading the page.
  requiresAdmin: true
</route>
